// -----------------------------------------------------------------------
// <copyright file="input-new-pswd.component.ts" company="AntemetA">
//     Copyright (c) AntemetA. All rights reserved.
// </copyright>
// -----------------------------------------------------------------------
import { Component, Optional, Inject, Injector, OnInit, ElementRef, OnDestroy } from '@angular/core';
import { NG_VALUE_ACCESSOR, NG_VALIDATORS, NG_ASYNC_VALIDATORS, } from '@angular/forms';

import { Subject, Subscription } from 'rxjs';
import { map, debounceTime, concatMap } from 'rxjs/operators';
import type { ZXCVBNScore } from 'zxcvbn';

import { InputBase } from '@ui/components/form/base/input-base';
import { ZxcvbnModule } from '@common/types/types';


@Component({
    selector: 'aa-input-new-pswd2',
    templateUrl: './input-new-pswd2.component.html',
    styleUrls: [
        '../input.component.scss',
        './input-new-pswd2.component.scss'
    ],
    providers: [{
            provide: NG_VALUE_ACCESSOR,
            useExisting: InputNewPswd2Component,
            multi: true,
        }],
    standalone: false
})
export class InputNewPswd2Component extends InputBase<string> implements OnInit, OnDestroy
{
    private static identifier: number = 0;
    public typeInput: string = 'password';
    public extra: string = 'fa-eye-slash';
    private showHide: boolean = false;
    private timer: any;
    public strengthMsgKey: string = '';
    public keyUp: Subject<any> = new Subject<any>();
    private subscription: Subscription;
    private indicators: NodeListOf<Element>;
    private strengthKeys: string[] = [
        'WORST',
        'BAD',
        'WEAK',
        'GOOD',
        'STRONG'
    ];
    private strengthClass: string[] = [
        'worst',
        'bad',
        'weak',
        'good',
        'strong'
    ];

    constructor (
        private elementRef: ElementRef,
        @Optional() @Inject(NG_VALIDATORS) validators: Array<any>,
        @Optional() @Inject(NG_ASYNC_VALIDATORS) asyncValidators: Array<any>,
        injector: Injector
    )
    {
        super(injector);

        this.fieldId = `input-new-pswd2-${InputNewPswd2Component.identifier++}-id`;
    }

    ngOnInit ()
    {
        this.indicators = this.elementRef.nativeElement.querySelector('.password-strength').querySelectorAll('.indicator');

        this.subscription = this.keyUp
            .pipe(
                map(event => event.target.value),
                debounceTime(250),
                // distinctUntilChanged(),
                concatMap((inputValue: string) => import('zxcvbn')
                    .then((zxcvbnModule: ZxcvbnModule) =>
                    {
                        this.passwordStrength(zxcvbnModule.default(inputValue).score);
                    })
                )
            )
            .subscribe();
    }

    ngOnDestroy (): void
    {
        clearTimeout(this.timer);
        this.subscription.unsubscribe();
    }

    viewPswd ()
    {
        this.showHide = !this.showHide;
        this.typeInput = this.showHide ? 'text' : 'password';
        if (this.showHide)
        {
            this.typeInput = 'text';
            this.extra = 'fa-eye';
            this.initiateTimer();
        }
        else
        {
            this.typeInput = 'password';
            this.extra = 'fa-eye-slash';
        }
    }

    private initiateTimer ()
    {
        if (this.timer)
        {
            clearTimeout(this.timer);
        }

        this.timer = setTimeout(() =>
        {
            if (this.showHide)
            {
                this.viewPswd();
            }
        }, 2500);
    }

    private passwordStrength (score: ZXCVBNScore): void
    {
        this.indicators.forEach((item, idx) =>
        {
            item.classList.remove(...this.strengthClass);
            if (idx <= score)
            {
                item.classList.add(this.strengthClass[score]);
            }
        });
        this.strengthMsgKey = `FORMS.PASSWORDSTRENGTH.${this.strengthKeys[score]}`;
    }
}
